/**
 * Get the worker URL from environment variable
 * @returns {string|null} - Worker URL or null if not available
 */
export function getWorkerUrl() {
  // Check for explicit environment variable
  if (import.meta.env.VITE_WORKER_URL) {
    return import.meta.env.VITE_WORKER_URL;
  }

  // In production, if worker is deployed as a separate service,
  // you'll need to set VITE_WORKER_URL in your environment variables
  // For now, return null to gracefully handle missing URL
  return null;
}

/**
 * Get the full API URL for a given path
 * If worker URL is configured, prepends it to the path
 * Otherwise, returns the relative path (for Pages Functions or local dev)
 * @param {string} path - API path (e.g., '/api/parkingSlots')
 * @returns {string} - Full API URL
 */
export function getApiUrl(path) {
  const workerUrl = getWorkerUrl();
  
  if (workerUrl) {
    // Remove leading slash from path if present, then append to worker URL
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${workerUrl}${cleanPath}`;
  }
  
  // Fallback to relative path (for Pages Functions or when worker is attached to Pages)
  return path;
}

/**
 * Wrapper around fetch that automatically clears localStorage and reloads the page
 * if the response is not ok (status not in 200-299 range)
 * @param {string|Request} url - The URL or Request object to fetch
 * @param {RequestInit} options - Fetch options (same as native fetch)
 * @returns {Promise<Response>} - The fetch response
 */
export async function apiFetch(url, options = {}) {
  const response = await fetch(url, options);
  
  if (!response.ok) {
    // Clear localStorage
    localStorage.clear();
    // Reload the page
    window.location.reload();
    // Return the response (though page will reload before this is used)
    return response;
  }
  
  return response;
}
